import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useIntl } from "react-intl";
import { Box, Typography, IconButton } from "@mui/material";
import { MapPin, X, ChevronDown, ChevronRight, Check } from "lucide-react";
import { searchPlaces } from "bundles/common/utils/locationCoordinates";
import {
  be_regions,
  lu_regions,
  fr_regions,
  companyIndustryMap,
  companySubSectorMap,
  jobStatuses,
  companySizeMap,
  jobOfferAdvantages,
} from "bundles/common/utils/constants";


const LABEL_SX = {
  fontSize: "10px",
  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "hsl(178, 10%, 50%)",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const pill = (active) => ({
  px: "12px", py: "6px", borderRadius: "9999px",
  fontSize: "12px", cursor: "pointer", border: "none",
  transition: "all 0.15s",
  fontFamily: active
    ? "Segoe UI Bold, sans-serif, system-ui"
    : "Segoe UI Regular, sans-serif, system-ui",
  bgcolor: active ? "hsl(178, 93%, 10%)" : "transparent",
  color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 10%, 45%)",
  outline: active ? "none" : "1px solid hsl(160, 10%, 90%)",
  "&:hover": {
    bgcolor: active ? "hsl(178, 93%, 10%)" : "hsla(160, 10%, 96%, 0.7)",
    color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 93%, 10%)",
  },
});

const sectorPill = (active, expanded) => ({
  display: "inline-flex", alignItems: "center", gap: "4px",
  px: "12px", py: "6px", borderRadius: "9999px",
  fontSize: "12px", cursor: "pointer", border: "none",
  transition: "all 0.15s",
  fontFamily: active
    ? "Segoe UI Bold, sans-serif, system-ui"
    : "Segoe UI Regular, sans-serif, system-ui",
  bgcolor: active
    ? "hsl(178, 93%, 10%)"
    : expanded
      ? "hsla(178, 93%, 10%, 0.07)"
      : "transparent",
  color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 10%, 45%)",
  outline: active || expanded ? "none" : "1px solid hsl(160, 10%, 90%)",
  "&:hover": {
    bgcolor: active ? "hsl(178, 93%, 10%)" : "hsla(160, 10%, 96%, 0.7)",
    color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 93%, 10%)",
  },
});

const checkboxSx = (active) => ({
  width: "15px", height: "15px", borderRadius: "4px",
  display: "flex", alignItems: "center", justifyContent: "center",
  border: active ? "none" : "1.5px solid hsl(160, 10%, 88%)",
  bgcolor: active ? "hsl(178, 93%, 10%)" : "transparent",
  transition: "all 0.15s", flexShrink: 0,
});

const FilterRow = ({ label, children, noDivider }) => (
  <Box>
    {!noDivider && (
      <Box sx={{ height: "1px", bgcolor: "hsla(160, 10%, 90%, 0.7)", mx: "-24px", mb: "20px" }} />
    )}
    <Box sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      gap: { xs: "10px", sm: "20px" },
      // xs must stretch: flex-start shrank every row (region search included)
      // to its content width on mobile.
      alignItems: { xs: "stretch", sm: "flex-start" },
      pb: "20px",
    }}>
      <Box sx={{ pt: "6px", width: { xs: "auto", sm: "110px" } }}>
        <Typography sx={LABEL_SX}>{label}</Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%", sm: "auto" } }}>
        {children}
      </Box>
    </Box>
  </Box>
);

const JobFilters = ({
  setFiltersOpen,
  regionSearch,
  setRegionSearch,
  filteredRegions,
  selectedRegions,
  toggleRegion,
  selectedLanguages,
  toggleLanguage,
  selectedIndustries,
  toggleIndustry,
  selectedSubcategories,
  setSelectedSubcategories,
  selectedJobStatuses,
  toggleJobStatus,
  selectedCompanySizes,
  toggleCompanySize,
  selectedBenefits,
  toggleBenefit,
  clearFilters,
  count,
  isMobile,
  distanceFilter,
  setDistanceFilter,
}) => {
  const intl = useIntl();
  const [expandedIndustry, setExpandedIndustry] = useState(null);
  const [expandedCountries, setExpandedCountries] = useState({ belgium: true, luxembourg: false, france: false });

  const benefitGroups = [
    { key: "advantages",   items: jobOfferAdvantages.advantages,  titleId: "job_offer_edit.advantages_title" },
    { key: "schedules",    items: jobOfferAdvantages.schedules,   titleId: "job_offer_edit.schedules_title" },
    { key: "environments", items: jobOfferAdvantages.environment, titleId: "job_offer_edit.environment_title" },
    { key: "primes",       items: jobOfferAdvantages.primes,      titleId: "job_offer_edit.prime_title" },
  ];

  /* location autocomplete --------------------------------------------------- */
  const [locationQuery, setLocationQuery] = useState(distanceFilter?.label || "");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const locationDebounceRef = useRef(null);
  const locationWrapperRef = useRef(null);
  const DISTANCE_OPTIONS = [0, 10, 25, 50, 100, 150];

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (locationWrapperRef.current && !locationWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
    if (!locationQuery || locationQuery.length < 2) { setLocationSuggestions([]); return; }
    locationDebounceRef.current = setTimeout(() => {
      searchPlaces(locationQuery, intl.locale).then((results) => {
        setLocationSuggestions(results);
        setShowSuggestions(results.length > 0);
      });
    }, 300);
    return () => clearTimeout(locationDebounceRef.current);
  }, [locationQuery]);

  const selectLocation = useCallback((place) => {
    setDistanceFilter((prev) => ({ lat: place.lat, lng: place.lng, km: prev?.km || 25, label: place.label }));
    setLocationQuery(place.label);
    setShowSuggestions(false);
  }, [setDistanceFilter]);

  const setDistanceKm = useCallback((km) => {
    setDistanceFilter((prev) => prev ? { ...prev, km } : prev);
  }, [setDistanceFilter]);

  /* data -------------------------------------------------------------------- */
  const languageOptions = ["french", "english", "dutch"].map((key) => ({
    key,
    label: intl.formatMessage({ id: `languages.${key}`, defaultMessage: key }),
  }));

  const contractOptions = jobStatuses.map((code) => ({
    key: code,
    label: intl.formatMessage({ id: `candidate.status_map.${code}`, defaultMessage: code }),
  }));

  const industryOptions = companyIndustryMap.map((key) => ({
    key,
    label: intl.formatMessage({ id: `company.company_industry.${key}`, defaultMessage: key }),
    hasSubs: !!(companySubSectorMap[key]?.length),
  }));

  const subcategories = expandedIndustry && companySubSectorMap[expandedIndustry]
    ? companySubSectorMap[expandedIndustry].map((sub) => ({
      key: sub,
      label: intl.formatMessage({ id: `company.company_sub_sector.${sub}`, defaultMessage: sub.replace(/_/g, " ") }),
    }))
    : [];

  const toggleSubcategory = (sub) => {
    setSelectedSubcategories((prev) =>
      prev.includes(sub) ? prev.filter((x) => x !== sub) : [...prev, sub]
    );
  };

  const countryGroups = useMemo(() => {
    const groups = [
      { key: "belgium", labelId: "countries.belgium", regions: be_regions },
      { key: "luxembourg", labelId: "countries.luxembourg", regions: lu_regions },
      { key: "france", labelId: "countries.france", regions: fr_regions },
    ];
    return groups
      .map((g) => ({ ...g, regions: g.regions.filter((r) => filteredRegions.includes(r)) }))
      .filter((g) => g.regions.length > 0);
  }, [filteredRegions]);

  const toggleCountryExpand = (key) =>
    setExpandedCountries((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleCountry = (countryRegions) => {
    const allSelected = countryRegions.every((r) => selectedRegions.includes(r));
    countryRegions.forEach((r) => {
      if (allSelected ? selectedRegions.includes(r) : !selectedRegions.includes(r)) toggleRegion(r);
    });
  };

  const hasActiveFilters = selectedRegions.length > 0
    || selectedLanguages.length > 0
    || selectedIndustries.length > 0
    || selectedSubcategories.length > 0
    || selectedJobStatuses.length > 0
    || selectedCompanySizes.length > 0
    || selectedBenefits.length > 0
    || (distanceFilter && distanceFilter.km > 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", maxHeight: isMobile ? "100vh" : "85vh" }}>

      {/* ── Header ── */}
      <Box sx={{
        px: "24px", py: "18px",
        borderBottom: "1px solid hsla(160, 10%, 90%, 0.7)",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexShrink: 0,
      }}>
        <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "17px", color: "hsl(178, 93%, 10%)" }}>
          {intl.formatMessage({ id: "candidate.jobs_tab.filters" })}
        </Typography>
        <IconButton onClick={() => setFiltersOpen(false)} size="small">
          <X size={17} color="hsl(178, 10%, 45%)" />
        </IconButton>
      </Box>

      {/* ── Scrollable body ── */}
      <Box sx={{ flex: 1, overflowY: "auto", px: "24px", pt: "20px" }}>

        {/* ── Distance ── */}
        <FilterRow label={intl.formatMessage({ id: "candidate.jobs_tab.panel.distance" })} noDivider>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: "10px", alignItems: { xs: "stretch", sm: "center" } }}>
            {/* City input */}
            <Box sx={{ position: "relative", flex: "0 0 auto", width: { xs: "100%", sm: "200px" } }} ref={locationWrapperRef}>
              <Box sx={{
                display: "flex", alignItems: "center", gap: "8px",
                bgcolor: "hsla(160, 10%, 96%, 0.6)", borderRadius: "10px", px: "12px", py: "7px",
                border: "1px solid hsl(160, 10%, 90%)",
                "&:focus-within": { borderColor: "hsla(153, 68%, 25%, 0.35)" },
              }}>
                <MapPin size={13} color="hsl(178, 10%, 50%)" />
                <Box
                  component="input"
                  value={locationQuery}
                  onChange={(e) => { setLocationQuery(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => locationSuggestions.length > 0 && setShowSuggestions(true)}
                  placeholder={intl.formatMessage({ id: "candidate.jobs_tab.panel.city_placeholder" })}
                  sx={{
                    flex: 1, border: "none", outline: "none", bgcolor: "transparent",
                    fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    color: "hsl(178, 93%, 10%)", minWidth: 0,
                    "&::placeholder": { color: "hsl(178, 10%, 55%)", opacity: 1 },
                  }}
                />
                {locationQuery && (
                  <Box component="button" onClick={() => { setLocationQuery(""); setLocationSuggestions([]); }}
                    sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex" }}>
                    <X size={12} color="hsl(178, 10%, 50%)" />
                  </Box>
                )}
              </Box>
              {showSuggestions && locationSuggestions.length > 0 && (
                <Box sx={{
                  position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 30,
                  bgcolor: "hsl(0, 0%, 100%)", border: "1px solid hsl(160, 10%, 90%)",
                  borderRadius: "10px", p: "4px",
                  boxShadow: "0 4px 16px hsla(178, 93%, 10%, 0.08)",
                  maxHeight: "180px", overflowY: "auto",
                }}>
                  {locationSuggestions.map((place, i) => (
                    <Box key={i} component="button" onClick={() => selectLocation(place)} sx={{
                      display: "flex", alignItems: "center", gap: "8px", width: "100%",
                      px: "10px", py: "7px", borderRadius: "8px", border: "none", cursor: "pointer",
                      bgcolor: "transparent", textAlign: "left",
                      fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      color: "hsl(178, 93%, 10%)",
                      "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.6)" },
                    }}>
                      <MapPin size={11} color="hsl(178, 10%, 50%)" style={{ flexShrink: 0 }} />
                      <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{place.label}</Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            {/* Distance pills */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {DISTANCE_OPTIONS.map((km) => (
                <Box key={km} component="button" onClick={() => setDistanceKm(km)} sx={pill((distanceFilter?.km || 0) === km)}>
                  {km === 0 ? intl.formatMessage({ id: "candidate.jobs_tab.panel.all" }) : `${km} km`}
                </Box>
              ))}
            </Box>
          </Box>
        </FilterRow>

        {/* ── Regions (mobile only) ── */}
        {isMobile && (
          <FilterRow label={intl.formatMessage({ id: "candidate.jobs_tab.panel.region_label" })}>
            <Box sx={{
              display: "flex", alignItems: "center", gap: "8px",
              bgcolor: "hsla(160, 10%, 96%, 0.6)", borderRadius: "10px", px: "12px", py: "7px",
              border: "1px solid hsl(160, 10%, 90%)", mb: "10px",
            }}>
              <MapPin size={13} color="hsl(178, 10%, 50%)" />
              <Box component="input" value={regionSearch} onChange={(e) => setRegionSearch(e.target.value)}
                placeholder={intl.formatMessage({ id: "candidate.jobs_tab.panel.region_search_placeholder" })}
                sx={{
                  flex: 1, border: "none", outline: "none", bgcolor: "transparent",
                  fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(178, 93%, 10%)",
                  "&::placeholder": { color: "hsl(178, 10%, 55%)", opacity: 1 },
                }}
              />
              {regionSearch && (
                <Box component="button" onClick={() => setRegionSearch("")}
                  sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex" }}>
                  <X size={12} color="hsl(178, 10%, 50%)" />
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {countryGroups.map((group) => {
                const expanded = expandedCountries[group.key];
                const selectedCount = group.regions.filter((r) => selectedRegions.includes(r)).length;
                const allSelected = selectedCount === group.regions.length;
                const someSelected = selectedCount > 0 && !allSelected;
                return (
                  <Box key={group.key} sx={{ borderRadius: "10px", border: "1px solid hsla(160, 10%, 90%, 0.6)", overflow: "hidden" }}>
                    <Box sx={{
                      display: "flex", alignItems: "center", gap: "8px", px: "12px", py: "8px", cursor: "pointer",
                      bgcolor: someSelected || allSelected ? "hsla(153, 68%, 25%, 0.05)" : "transparent",
                      "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.5)" },
                    }} onClick={() => toggleCountryExpand(group.key)}>
                      {expanded ? <ChevronDown size={13} color="hsl(178, 10%, 50%)" /> : <ChevronRight size={13} color="hsl(178, 10%, 50%)" />}
                      <Typography sx={{ flex: 1, fontSize: "12px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)" }}>
                        {intl.formatMessage({ id: group.labelId, defaultMessage: group.key })}
                      </Typography>
                      {selectedCount > 0 && (
                        <Box sx={{
                          fontSize: "9px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                          bgcolor: "hsl(153, 68%, 25%)", color: "hsl(0, 0%, 100%)",
                          borderRadius: "9999px", minWidth: "16px", height: "16px",
                          display: "flex", alignItems: "center", justifyContent: "center", px: "4px",
                        }}>{selectedCount}</Box>
                      )}
                      <Box component="button" type="button" onClick={(e) => { e.stopPropagation(); toggleCountry(group.regions); }}
                        sx={{
                          width: "17px", height: "17px", borderRadius: "5px",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          border: allSelected || someSelected ? "none" : "1.5px solid hsl(160, 10%, 90%)",
                          bgcolor: allSelected ? "hsl(153, 68%, 25%)" : someSelected ? "hsla(153, 68%, 25%, 0.3)" : "transparent",
                          cursor: "pointer", p: 0,
                        }}>
                        {(allSelected || someSelected) && <Check size={9} color="hsl(0, 0%, 100%)" strokeWidth={3} />}
                      </Box>
                    </Box>
                    {expanded && group.regions.map((r) => {
                      const active = selectedRegions.includes(r);
                      return (
                        <Box key={r} component="button" onClick={() => toggleRegion(r)} sx={{
                          display: "flex", alignItems: "center", gap: "8px", width: "100%",
                          px: "12px", pl: "32px", py: "6px", border: "none", cursor: "pointer",
                          bgcolor: active ? "hsla(153, 68%, 25%, 0.05)" : "transparent",
                          "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.5)" },
                        }}>
                          <Box sx={checkboxSx(active)}>
                            {active && <Check size={8} color="hsl(0, 0%, 100%)" strokeWidth={3} />}
                          </Box>
                          <Typography sx={{ fontSize: "11px", color: "hsl(178, 93%, 10%)", fontFamily: active ? "Segoe UI Semibold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui" }}>
                            {intl.formatMessage({ id: `regions.${r}`, defaultMessage: r })}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </FilterRow>
        )}

        {/* ── Secteur ── */}
        <FilterRow label={intl.formatMessage({ id: "candidate.jobs_tab.panel.sector" })}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {/* Tous */}
            <Box component="button" onClick={() => {
              selectedIndustries.forEach((ind) => toggleIndustry(ind));
              setSelectedSubcategories([]);
              setExpandedIndustry(null);
            }} sx={pill(selectedIndustries.length === 0)}>
              {intl.formatMessage({ id: "candidate.jobs_tab.panel.all" })}
            </Box>
            {industryOptions.map((opt) => {
              const active = selectedIndustries.includes(opt.key);
              const expanded = expandedIndustry === opt.key;
              return (
                <Box
                  key={opt.key}
                  component="button"
                  onClick={() => {
                    toggleIndustry(opt.key);
                    setExpandedIndustry(expanded ? null : opt.key);
                  }}
                  sx={sectorPill(active, expanded)}
                >
                  {opt.label}
                  {opt.hasSubs && (
                    <ChevronDown
                      size={11}
                      style={{
                        transition: "transform 0.2s",
                        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                        opacity: 0.6,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Subcategories panel */}
          {expandedIndustry && subcategories.length > 0 && (
            <Box sx={{
              mt: "12px",
              p: "14px 16px",
              borderRadius: "12px",
              bgcolor: "hsla(160, 10%, 96%, 0.5)",
              border: "1px solid hsla(160, 10%, 90%, 0.8)",
            }}>
              <Typography sx={{ ...LABEL_SX, mb: "10px", display: "block" }}>
                {intl.formatMessage({ id: `company.company_industry.${expandedIndustry}`, defaultMessage: expandedIndustry })}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {subcategories.map((sub) => {
                  const active = selectedSubcategories.includes(sub.key);
                  return (
                    <Box key={sub.key} component="button" onClick={() => toggleSubcategory(sub.key)} sx={{
                      display: "inline-flex", alignItems: "center", gap: "6px",
                      px: "10px", py: "5px", borderRadius: "9999px",
                      fontSize: "12px", cursor: "pointer", border: "none", transition: "all 0.15s",
                      fontFamily: active ? "Segoe UI Semibold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui",
                      bgcolor: active ? "hsl(178, 93%, 10%)" : "hsl(0, 0%, 100%)",
                      color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 10%, 40%)",
                      outline: active ? "none" : "1px solid hsl(160, 10%, 88%)",
                      "&:hover": { bgcolor: active ? "hsl(178, 93%, 10%)" : "hsla(160, 10%, 94%, 0.8)", color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 93%, 10%)" },
                    }}>
                      {active && <Check size={9} strokeWidth={3} />}
                      {sub.label}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </FilterRow>

        {/* ── Type de contrat ── */}
        <FilterRow label={intl.formatMessage({ id: "candidate.jobs_tab.panel.contract" })}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            <Box component="button"
              onClick={() => selectedJobStatuses.length > 0 && selectedJobStatuses.forEach((s) => toggleJobStatus(s))}
              sx={pill(selectedJobStatuses.length === 0)}>
              {intl.formatMessage({ id: "candidate.jobs_tab.panel.all" })}
            </Box>
            {contractOptions.map((opt) => (
              <Box key={opt.key} component="button" onClick={() => toggleJobStatus(opt.key)} sx={pill(selectedJobStatuses.includes(opt.key))}>
                {opt.label}
              </Box>
            ))}
          </Box>
        </FilterRow>

        {/* ── Langues ── */}
        <FilterRow label={intl.formatMessage({ id: "candidate.jobs_tab.panel.languages" })}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {languageOptions.map((opt) => (
              <Box key={opt.key} component="button" onClick={() => toggleLanguage(opt.key)} sx={pill(selectedLanguages.includes(opt.key))}>
                {opt.label}
              </Box>
            ))}
          </Box>
        </FilterRow>

        {/* ── Taille d'entreprise ── */}
        <FilterRow label={intl.formatMessage({ id: "candidate.jobs_tab.panel.company_size" })}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            <Box component="button"
              onClick={() => selectedCompanySizes.length > 0 && selectedCompanySizes.forEach((s) => toggleCompanySize(s))}
              sx={pill(selectedCompanySizes.length === 0)}>
              {intl.formatMessage({ id: "candidate.jobs_tab.panel.all_feminine" })}
            </Box>
            {companySizeMap.map((size) => (
              <Box key={size} component="button" onClick={() => toggleCompanySize(size)} sx={pill(selectedCompanySizes.includes(size))}>
                {intl.formatMessage({ id: `company.company_size.options.${size}`, defaultMessage: size.replace(/_/g, " ") })}
              </Box>
            ))}
          </Box>
        </FilterRow>

        {/* ── Avantages ── */}
        <FilterRow label={intl.formatMessage({ id: "candidate.jobs_tab.panel.benefits" })}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {benefitGroups.map((group) => (
              <Box key={group.key} sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: "6px", sm: "12px" }, alignItems: { xs: "flex-start", sm: "flex-start" } }}>
                <Typography sx={{
                  fontSize: "11px",
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  color: "hsl(178, 10%, 50%)",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  pt: "7px",
                  width: { xs: "auto", sm: "88px" },
                }}>
                  {intl.formatMessage({ id: group.titleId, defaultMessage: group.key })}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {group.items.map((key) => (
                    <Box key={key} component="button" onClick={() => toggleBenefit(key)} sx={pill(selectedBenefits.includes(key))}>
                      {intl.formatMessage({ id: `company_view.profile.advantages.items.${key}`, defaultMessage: key.replace(/_/g, " ") })}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </FilterRow>

        {/* bottom breathing room */}
        <Box sx={{ height: "8px" }} />
      </Box>

      {/* ── Footer ── */}
      <Box sx={{
        px: "24px", py: "14px",
        borderTop: "1px solid hsla(160, 10%, 90%, 0.7)",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexShrink: 0,
      }}>
        {hasActiveFilters ? (
          <Box component="button" onClick={clearFilters} sx={{
            border: "none", bgcolor: "transparent", cursor: "pointer",
            fontSize: "13px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            color: "hsl(178, 10%, 50%)",
            "&:hover": { color: "hsl(178, 93%, 10%)" },
          }}>
            {intl.formatMessage({ id: "candidate.jobs_tab.reset" })}
          </Box>
        ) : <Box />}
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <Typography sx={{ fontSize: "13px", color: "hsl(178, 10%, 50%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
            {intl.formatMessage({ id: "candidate.jobs_tab.panel.results_count" }, { count })}
          </Typography>
          <Box component="button" onClick={() => setFiltersOpen(false)} sx={{
            px: "22px", py: "9px", borderRadius: "999px", border: "none", cursor: "pointer",
            bgcolor: "hsl(153, 68%, 25%)", color: "hsl(0, 0%, 100%)",
            fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "13px",
            "&:hover": { bgcolor: "hsla(153, 68%, 25%, 0.88)" },
            transition: "background 0.15s",
          }}>
            {intl.formatMessage({ id: "candidate.jobs_tab.panel.apply" })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default JobFilters;
